// Client member-api bertipe dari kontrak `@folaplus/contracts` (sumber yang sama dengan OpenAPI).
// Runtime hanya memakai metadata rute tanpa zod (bundle kecil); tipe diambil dari skema lengkap.
import type { MemberRouteName, MemberRoutes } from '@folaplus/contracts';
import { memberRouteMeta, type RouteMeta } from '@folaplus/contracts/route-meta';
import type { z } from 'zod';

type RouteOf<N extends MemberRouteName> = MemberRoutes[N];
export type ResponseOf<N extends MemberRouteName> = z.output<RouteOf<N>['response']>;
export type BodyOf<N extends MemberRouteName> =
  RouteOf<N> extends { body: infer B extends z.ZodType } ? z.input<B> : never;
export type QueryOf<N extends MemberRouteName> =
  RouteOf<N> extends { query: infer Q extends z.ZodType } ? z.input<Q> : never;

/** Nama parameter `:x` dari path rute. */
type ParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | ParamNames<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;
type ParamsOf<N extends MemberRouteName> = [ParamNames<RouteOf<N>['path']>] extends [never]
  ? { params?: never }
  : { params: Record<ParamNames<RouteOf<N>['path']>, string> };

export type CallArgs<N extends MemberRouteName> = ParamsOf<N> & {
  body?: BodyOf<N>;
  query?: QueryOf<N>;
  file?: File;
  /** Wajib untuk rute idempoten; buat sekali per niat pengguna (mis. per layar konfirmasi). */
  idempotencyKey?: string;
  signal?: AbortSignal;
};

/** Error API dalam bentuk problem+json; `message` sudah dalam Bahasa Indonesia. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly fieldErrors: Record<string, string> = {},
    /** `details` problem+json selain error per field, mis. id tagihan yang masih pending. */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const NETWORK_ERROR_MESSAGE =
  'Tidak dapat terhubung ke server. Periksa koneksi internet lalu coba lagi.';

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'Terjadi kesalahan. Silakan coba lagi.';
}

// ------------------------------------------------------------------ sesi (access token di memori)

export type SessionStatus = 'unknown' | 'authenticated' | 'anonymous';
let accessToken: string | null = null;
let status: SessionStatus = 'unknown';
const listeners = new Set<() => void>();

function setSession(token: string | null): void {
  accessToken = token;
  status = token ? 'authenticated' : 'anonymous';
  for (const listener of listeners) listener();
}

export const session = {
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
  status: (): SessionStatus => status,
  signIn: (token: string) => setSession(token),
  signOut: () => setSession(null),
};

const CSRF_HEADER = { 'X-Requested-With': 'folaplus' };
let refreshing: Promise<boolean> | null = null;

/** Mode mock dev (lib/dev-mock.ts); konstanta build dari vite.config.ts, selalu false di build production. */
const MOCK_API = __FOLAPLUS_MOCK_API__;

/** Tukar cookie refresh (HttpOnly, path /auth) dengan access token baru; satu permintaan sekaligus. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    if (MOCK_API) {
      const { MOCK_TOKEN } = await import('./dev-mock');
      setSession(MOCK_TOKEN);
      refreshing = null;
      return true;
    }
    try {
      const response = await fetch(memberRouteMeta.refresh.path, {
        method: 'POST',
        credentials: 'same-origin',
        headers: CSRF_HEADER,
      });
      if (!response.ok) {
        setSession(null);
        return false;
      }
      const body = (await response.json()) as ResponseOf<'refresh'>;
      setSession(body.accessToken);
      return true;
    } catch {
      // Gangguan jaringan: jangan hapus sesi yang mungkin masih valid.
      if (status === 'unknown') setSession(null);
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

// ------------------------------------------------------------------ panggilan

function buildUrl(path: string, params?: Record<string, string>, query?: unknown): string {
  const filled = path.replace(/:(\w+)/g, (_m, key: string) => encodeURIComponent(params?.[key] ?? ''));
  const search = new URLSearchParams();
  // Query kontrak hanya berisi string/number (nominal rupiah dikirim sebagai string digit).
  for (const [key, value] of Object.entries((query ?? {}) as Record<string, string | number | undefined>)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${filled}?${qs}` : filled;
}

interface ProblemBody {
  title?: unknown;
  code?: unknown;
  details?: unknown;
}

function translateErrorMessage(msg: string): string {
  if (msg.includes('Too small: expected string to have >=')) {
    const match = msg.match(/>=\s*(\d+)/);
    const min = match ? match[1] : '1';
    return `Wajib diisi (minimal ${min} karakter).`;
  }
  if (msg.toLowerCase().includes('invalid email'))
    return 'Format email tidak valid (contoh: nama@email.com).';
  if (msg.toLowerCase().includes('invalid phone'))
    return 'Nomor WhatsApp tidak valid (format +62, mis. +6281234567890).';
  if (msg.toLowerCase().includes('required')) return 'Kolom ini wajib diisi.';
  return msg;
}

/** application/problem+json (docs/architecture.md bagian 6.1) → ApiError. */
async function toApiError(response: Response): Promise<ApiError> {
  const body = (await response.json().catch(() => null)) as ProblemBody | null;
  if (!body || typeof body.title !== 'string' || typeof body.code !== 'string') {
    return new ApiError(
      response.status,
      'HTTP_ERROR',
      `Server mengembalikan kesalahan (${response.status}).`,
    );
  }
  const fieldErrors: Record<string, string> = {};
  if (Array.isArray(body.details)) {
    for (const detail of body.details as { path?: unknown; message?: unknown }[]) {
      if (
        typeof detail.path === 'string' &&
        typeof detail.message === 'string' &&
        !fieldErrors[detail.path]
      ) {
        fieldErrors[detail.path] = translateErrorMessage(detail.message);
      }
    }
  }
  const details =
    body.details && typeof body.details === 'object' && !Array.isArray(body.details)
      ? (body.details as Record<string, unknown>)
      : {};
  return new ApiError(response.status, body.code, body.title, fieldErrors, details);
}

export async function api<N extends MemberRouteName>(name: N, args: CallArgs<N> = {} as CallArgs<N>) {
  if (MOCK_API) {
    const mocked = (await import('./dev-mock')).mockApi(
      name,
      args as { query?: { wallet?: string }; params?: Record<string, string> },
    );
    if (!mocked) {
      throw new ApiError(
        501,
        'MOCK_NOT_AVAILABLE',
        'Mode mock: aksi ini butuh backend dan tidak dijalankan.',
      );
    }
    return mocked;
  }
  const route: RouteMeta = memberRouteMeta[name];
  const url = buildUrl(route.path, args.params, args.query);

  const send = (): Promise<Response> => {
    const headers: Record<string, string> = { ...CSRF_HEADER };
    if (route.auth && accessToken) headers.Authorization = `Bearer ${accessToken}`;
    if (args.idempotencyKey) headers['Idempotency-Key'] = args.idempotencyKey;
    let body: BodyInit | undefined;
    if (args.file) {
      const form = new FormData();
      form.append('file', args.file);
      body = form;
    } else if (route.method !== 'GET') {
      headers['Content-Type'] = 'application/json';
      body = JSON.stringify(args.body ?? {});
    }
    return fetch(url, {
      method: route.method,
      headers,
      body,
      credentials: 'same-origin',
      signal: args.signal,
    });
  };

  let response: Response;
  try {
    response = await send();
    if (response.status === 401 && route.auth && (await refreshSession())) response = await send();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    throw new ApiError(0, 'NETWORK_ERROR', NETWORK_ERROR_MESSAGE);
  }
  if (!response.ok) {
    if (response.status === 401 && route.auth) setSession(null);
    throw await toApiError(response);
  }
  if (response.status === 204) return null as ResponseOf<N>;
  return (await response.json()) as ResponseOf<N>;
}

/** `identifier` = email atau nomor HP format +62 (kontrak login 19 Okt 2026). */
export async function login(identifier: string, password: string): Promise<void> {
  const result = await api('login', { body: { identifier, password } });
  setSession(result.accessToken);
}

export async function logout(): Promise<void> {
  try {
    await api('logout');
  } finally {
    setSession(null);
  }
}
